import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { MutationFunction } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import {
  DefaultStoreProjectAssigneeListItem,
  DefaultStoreProjectAssigneeUpdate,
  DefaultStoreProjectAssigneeUpdateResponse,
} from "@/types/defaultStoreProjectAssignee";
import { ApiError } from "@/services/api";
import { DefaultStoreProjectAssigneesService } from "@/services/defaultStoreProjectAssignees";
import { useDefaultStoreProjectAssigneeId } from "@/modules/defaultStoreProjectAssignees/hooks/useDefaultStoreProjectAssigneeId";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";

export const useUpdateDefaultStoreProjectAssignee = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const id = useDefaultStoreProjectAssigneeId();
  const { invalidateListQuery } = useQueryCacheUpdater(
    Module.DefaultStoreProjectAssignees,
  );

  const mutationFn: MutationFunction<
    DefaultStoreProjectAssigneeUpdateResponse,
    DefaultStoreProjectAssigneeUpdate
  > = async (values) => {
    const response = await DefaultStoreProjectAssigneesService.update(
      id,
      values,
    );
    return response;
  };

  const mutation = useMutationWithErrorHandling<
    DefaultStoreProjectAssigneeListItem,
    ApiError,
    DefaultStoreProjectAssigneeUpdate,
    unknown
  >({
    mutationFn,
    onSuccess: () => {
      toast({
        title: t("defaultStoreProjectAssignees.success.update"),
      });
      invalidateListQuery();
    },
    errorTitle: t("defaultStoreProjectAssignees.errors.update"),
  });

  return mutation;
};
